// ============================================================================
// The page's smart default window (R22, 2026-08-20 Miles).
//
// R20/R21 focused the live view on a readable recent slice, but kept it as an
// OVERLAY on a "last 7 days" selection — the 7-days chip stayed lit while the
// graph showed 3.4 days, and the page needed "show full range" escape hatches
// everywhere. Miles's R22 rule: the smart window is fine as a default, but it
// must BE the selection — the custom chip lights up with the real timeline,
// the calendar opens prefilled with it, and the graph draws exactly that
// window. So this module no longer narrows a horizon at render time; it
// derives the DEFAULT RANGE the page lands on, once, and the ordinary range
// machinery does the rest.
//
// The derivation is unchanged from the approved R20 math: how many
// comfortably-readable bars fit this viewport, times the cadence a 7-day
// horizon would get — ≈3.4 days on a desktop, ≈6 days on a phone. If that
// would not be meaningfully narrower than the horizon itself, the default is
// simply the honest "last 7 days" preset.
// ============================================================================

import {
  autoBarBudget,
  bucketWidthMs,
  optimalFocusBars,
  pickAutoBucket,
} from './activityTheme';

/**
 * A smart default has to be MEANINGFULLY narrower than the horizon, or the
 * page would land on a custom window that shows essentially the whole preset
 * anyway. 1.35 keeps it honest (a 7-day/per-hour horizon at 168 bars defaults
 * to its recent 82; a 90-bar horizon just lands on "last 7 days").
 */
export const FOCUS_MIN_RATIO = 1.35;

/** The landing horizon the smart default is derived from. */
export const SMART_HORIZON_DAYS = 7;

export type SmartDefaultRange =
  | { kind: 'smart'; spanMs: number }
  | { kind: 'preset'; days: number };

/**
 * The range the page lands on. `smart` is a live, now-anchored window of
 * `spanMs` — presented as (and convertible into) an ordinary custom range;
 * the preset fallback fires when narrowing would be a technicality.
 */
export function smartDefaultRange(viewportWidth?: number): SmartDefaultRange {
  const horizonMs = SMART_HORIZON_DAYS * 86_400_000;
  const bucket = pickAutoBucket(horizonMs, autoBarBudget(viewportWidth));
  const width = bucketWidthMs(bucket);
  const bars = optimalFocusBars(viewportWidth);
  const horizonBars = width > 0 ? Math.ceil(horizonMs / width) : 0;
  if (!(width > 0) || horizonBars <= bars * FOCUS_MIN_RATIO) {
    return { kind: 'preset', days: SMART_HORIZON_DAYS };
  }
  return { kind: 'smart', spanMs: Math.min(horizonMs, bars * width) };
}

/**
 * "18 hours" / "3.4 days" / "12 days" — the page's lowercase voice for a
 * duration the user is being told about ("recent 3.4 days").
 * Sub-48h reads in hours because "1.8 days" is a worse answer than "44 hours".
 */
export function spanLabel(spanMs: number): string {
  const ms = Math.max(0, Number(spanMs) || 0);
  const hours = ms / 3_600_000;
  if (hours < 1) {
    const minutes = Math.max(1, Math.round(ms / 60_000));
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  if (hours < 48) {
    const h = Math.max(1, Math.round(hours));
    return `${h} hour${h === 1 ? '' : 's'}`;
  }
  const days = ms / 86_400_000;
  return `${days >= 10 ? Math.round(days) : Math.round(days * 10) / 10} days`;
}
